import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { UserIcon, AcademicCapIcon, StarIcon, PencilIcon, CheckIcon, XMarkIcon, CameraIcon, BriefcaseIcon, BookOpenIcon, ClockIcon, UserGroupIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon, CheckBadgeIcon } from '@heroicons/react/24/solid'
import api from '../../config/api'
import UserAvatar from '../../components/UserAvatar'

interface TeacherData {
  firstName: string
  lastName: string
  email: string
  phone: string
  title: string
  introduction: string
  specialties: string[]
  education: string
  experience: string
  averageRating: number
  totalHours: number
  totalStudents: number
}

const TeacherProfile = () => {
  const { user } = useAuthStore()
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ averageRating: 0, totalHours: 0, totalStudents: 0 })
  const [formData, setFormData] = useState<TeacherData>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    title: '',
    introduction: '',
    specialties: [],
    education: '',
    experience: '',
    averageRating: 0,
    totalHours: 0,
    totalStudents: 0,
  })

  useEffect(() => {
    fetchTeacherProfile()
  }, [])

  const fetchTeacherProfile = async () => {
    try {
      setLoading(true)
      const [profileRes, statsRes] = await Promise.all([
        api.get('/teachers/me/profile').catch(() => null),
        api.get('/bookings/teacher').catch(() => null)
      ])

      if (profileRes?.data) {
        const profile = profileRes.data
        setFormData(prev => ({
          ...prev,
          title: profile.title || '',
          introduction: profile.introduction || '',
          specialties: profile.specialties || [],
          education: profile.education || '',
          experience: profile.experience || '',
        }))
      }

      if (statsRes?.data) {
        const bookings = Array.isArray(statsRes.data) ? statsRes.data : []
        const completedBookings = bookings.filter((b: any) => b.status === 'completed')
        const totalHours = completedBookings.reduce((sum: number, b: any) => sum + (b.duration || 0), 0)
        const uniqueStudents = new Set(completedBookings.map((b: any) => b.studentId)).size
        const ratings = completedBookings.filter((b: any) => b.rating).map((b: any) => b.rating)
        const avgRating = ratings.length > 0 ? ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length : 0
        
        setStats({
          averageRating: avgRating,
          totalHours: totalHours,
          totalStudents: uniqueStudents
        })
      }
    } catch (error) {
      console.error('获取教师资料失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = () => {
    // 保存逻辑
    setIsEditing(false)
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-10 px-4">
        <div className="animate-pulse">
          <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-3xl h-48 mb-6"></div>
          <div className="bg-white rounded-2xl p-6 space-y-4">
            <div className="h-6 w-48 bg-gray-200 rounded"></div>
            <div className="h-4 w-full bg-gray-100 rounded"></div>
            <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      {/* 高级头部卡片 */}
      <div className="relative bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl overflow-hidden mb-8">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-400/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        </div>
        
        <div className="relative p-8 lg:p-10">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* 头像区域 */}
            <div className="relative">
              <div className="w-28 h-28 lg:w-32 lg:h-32 rounded-2xl bg-white/10 backdrop-blur-sm p-1.5">
                <UserAvatar
                  avatar={user?.avatar}
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  size="lg"
                  className="w-full h-full rounded-xl"
                />
              </div>
              <button className="absolute -bottom-2 -right-2 w-10 h-10 bg-white rounded-xl shadow-lg flex items-center justify-center text-gray-600 hover:text-indigo-600 transition-colors">
                <CameraIcon className="w-5 h-5" />
              </button>
            </div>
            
            {/* 信息区域 */}
            <div className="flex-1 text-white">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{formData.firstName} {formData.lastName}</h1>
                <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium flex items-center gap-1">
                  <CheckBadgeIcon className="w-4 h-4" /> 认证导师
                </span>
              </div>
              <p className="text-white/80 text-lg mb-4">{formData.title || '专业导师'}</p>
              
              {/* 评分 */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => (
                    <StarSolidIcon key={i} className={`w-5 h-5 ${i <= Math.round(stats.averageRating) ? 'text-amber-400' : 'text-white/30'}`} />
                  ))}
                  <span className="ml-2 font-semibold">{stats.averageRating.toFixed(1)}</span>
                </div>
              </div>
            </div>
            
            {/* 操作按钮 */}
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center px-6 py-3 text-sm font-semibold rounded-xl text-indigo-600 bg-white shadow-lg hover:shadow-xl transition-all"
                  >
                    <CheckIcon className="w-4 h-4 mr-2" />
                    保存
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="inline-flex items-center px-6 py-3 text-sm font-semibold rounded-xl text-white bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all"
                  >
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    取消
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-6 py-3 text-sm font-semibold rounded-xl text-indigo-600 bg-white shadow-lg hover:shadow-xl transition-all"
                >
                  <PencilIcon className="w-4 h-4 mr-2" />
                  编辑资料
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 -mt-4 mb-8 relative z-10">
        {[
          { label: '学生总数', value: stats.totalStudents, icon: UserGroupIcon, color: 'from-blue-500 to-cyan-500' },
          { label: '授课时长', value: `${stats.totalHours}h`, icon: ClockIcon, color: 'from-emerald-500 to-teal-500' },
          { label: '课程数量', value: formData.specialties.length || 0, icon: BookOpenIcon, color: 'from-purple-500 to-pink-500' },
          { label: '综合评分', value: stats.averageRating.toFixed(1), icon: StarIcon, color: 'from-amber-500 to-orange-500' }
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all group">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
              <stat.icon className="w-5 h-5 text-white" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
            <div className="text-sm text-gray-500">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        {/* 基本信息 - 高级版 */}
        <div className="bg-white shadow-lg rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
            <h2 className="text-lg font-bold text-gray-900 flex items-center">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center mr-3">
                <UserIcon className="h-4 w-4 text-white" />
              </div>
              基本信息
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">姓名</label>
                {isEditing ? (
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className="block w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                      placeholder="姓"
                    />
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      className="block w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                      placeholder="名"
                    />
                  </div>
                ) : (
                  <p className="text-base font-medium text-gray-900">{formData.firstName} {formData.lastName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">邮箱</label>
                {isEditing ? (
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="block w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                  />
                ) : (
                  <p className="text-base font-medium text-gray-900">{formData.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">手机号</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="block w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                  />
                ) : (
                  <p className="text-base font-medium text-gray-900">{formData.phone || '未设置'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">职称</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="block w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                  />
                ) : (
                  <p className="text-base font-medium text-gray-900">{formData.title || '未设置'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 教学信息 - 高级版 */}
        <div className="bg-white shadow-lg rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
            <h2 className="text-lg font-bold text-gray-900 flex items-center">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mr-3">
                <AcademicCapIcon className="h-4 w-4 text-white" />
              </div>
              <AcademicCapIcon className="h-5 w-5 mr-2 text-gray-400" />
              教学信息
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">个人简介</label>
                {isEditing ? (
                  <textarea
                    rows={4}
                    value={formData.introduction}
                    onChange={(e) => setFormData({...formData, introduction: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                ) : (
                  <p className="mt-1 text-sm text-gray-900">{formData.introduction}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">专业领域</label>
                <div className="mt-1">
                  {formData.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">教育背景</label>
                <p className="mt-1 text-sm text-gray-900">{formData.education}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">教学经验</label>
                <p className="mt-1 text-sm text-gray-900">{formData.experience}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 教学统计 */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <StarIcon className="h-5 w-5 mr-2 text-gray-400" />
              教学统计
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.averageRating.toFixed(1)}</div>
                <div className="text-sm text-gray-500">平均评分</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{stats.totalHours}</div>
                <div className="text-sm text-gray-500">总授课时长</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{stats.totalStudents}</div>
                <div className="text-sm text-gray-500">学生总数</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeacherProfile
